//Library imports
import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useLocation } from 'react-router-dom';

//Components
import Layout from '../../components/layout';
import SubmitButton from '../../components/submitButton';
import { Notification } from '../../components/notifications';
import FetchInput from '../../components/FetchInput';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getManualDuplicates, mergeStudents } from '../../api/services/studentService';
import Modal from '../../components/modal';

import StudentCustomMergRequest from '../../api/requests/studentCustomMergRequest';
import StudentMerge from '../../api/models/studentMerge';
import StudentMergeModel from '../../components/modal/studentMergeModel';
import moment from 'moment';

//Get our view state
type ViewState = {
    message?: string;
};


/**
 * Primary Component
 */
const ManualMerge = () => {
    //App hooks
    const history = useHistory();
    const { state } = useLocation<ViewState>();

    //Data hooks

    const [openMerge, setOpenMerge] = useState<boolean>(false);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [studentFrom, setStudentFrom] = useState<StudentMerge>();
    const [studentTo, setStudentTo] = useState<StudentMerge>();

    const [studentsData, setStudentsData] = useState<StudentMerge[]>([]);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [dobDate, setDobDate] = useState<Date>();
    const [districtName, setDistrictName] = useState<string>('');

    const [message, setMessage] = useState<string>(state?.message ?? '');
    const [mergeErrors, setMergeErrors] = useState<string[]>([]);
    const [isSuccess, setIsSuccess] = useState<boolean>(true);

    const getDuplicatesDefer = async (args: any[]) => {
        if (firstName !== '') {
            const request: StudentCustomMergRequest = {
                firstName: firstName,
                lastName: lastName,
                dob: dobDate,
                districtName: districtName
            }
            return await getManualDuplicates(request);
        }
    }

    const getDuplicatesAsync = useAsync({
        deferFn: getDuplicatesDefer,
        onResolve: (r) => {
            if (r != undefined) {
                setStudentsData(r.data.studentMerge);
            }
        },
    });

    const mergeStudentsDefer = async (args: any[]) => {
        if (studentFrom !== undefined && studentTo !== undefined) {
            setIsPending(true);
            return await mergeStudents({
                studentFrom: studentFrom!.studentId!,
                studentTo: studentTo!.studentId!
            });
        }
    }

    const mergeStudentsAsync = useAsync({
        deferFn: mergeStudentsDefer,
        onResolve: (r) => {
            if (r?.data?.item1 == true) {
                var filtered = studentsData.filter(x => x.studentId !== studentFrom?.studentId && x.studentId !== studentFrom?.studentId);
                setStudentsData(filtered);
                setIsSuccess(true);
                setMessage(r?.data?.item2 as string);
                setMergeErrors(r?.data?.item3 as string[]);
                setStudentFrom(undefined);
                setStudentTo(undefined);
                setOpenMerge(false);
                setIsPending(false);
            }
            else {
                setOpenMerge(false);
                setIsSuccess(false);
                setMessage(r?.data?.item2 as string);
                setMergeErrors(r?.data?.item3 as string[]);
                setIsPending(false);
            }

        },
    });

    useEffect(() => {
    }, [studentFrom, studentTo]);

    //If student is selected either Keep Or discard don't show any button
    const checkState = (stuid?: number) => {
        if (studentFrom?.studentId === stuid || studentTo?.studentId === stuid) {
            return false;
        }
        else {
            return true;
        }
    };

    const resetFields = () => {
        setFirstName('');
        setLastName('');
        setDobDate(undefined);
        setStudentTo(undefined);
        setStudentFrom(undefined);
        setDistrictName('');
        setMessage('');
    };

    return (
        <Layout isLoading={
            getDuplicatesAsync.isPending || isPending
        }>
            <Fragment>
                <Modal
                    saveText='Merge'
                    open={openMerge}
                    allowSave ={studentTo !== undefined && studentFrom !== undefined}
                    onCancel={() => {
                        setOpenMerge(false);
                    }}
                    onSave={() => {
                        mergeStudentsAsync.run();
                    }}
                    extraWide={true}
                >
                    <StudentMergeModel
                        studentFrom={studentFrom}
                        setStudentFrom={setStudentFrom}
                        studentTo={studentTo}
                        setStudentTo={setStudentTo}
                    ></StudentMergeModel>
                </Modal>
                {message && <Notification onClose={() => {
                    setMessage('');
                    setMergeErrors([]);
                }} isSuccess={isSuccess} ><div>
                        <p>{message}</p>
                        {!isSuccess && mergeErrors.length > 0 && (
                            <ul className="list-disc pl-5 mt-2">
                                {mergeErrors.map((err, idx) => (
                                    <li key={idx} className="text-sm">{err}</li>
                                ))}
                            </ul>
                        )}
                    </div></Notification>}

                {/* Primary Container */}
                <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                    {/* Search Fields */}
                    <div className='z-0 grid grid-cols-6 gap-6'>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FetchInput
                                value={firstName}
                                rounded='sm'
                                placeholder=''
                                label={`First Name`}
                                key={"student_ac"}
                                onClear={() => setFirstName('')}
                                onChange={(value: string) => setFirstName(value)}
                                icon={'search'}
                                required={true}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FetchInput
                                value={lastName}
                                rounded='sm'
                                placeholder=''
                                label={`Last Name`}
                                key={"student_ac"}
                                onClear={() => setLastName('')}
                                onChange={(value: string) => setLastName(value)}
                                icon={'search'}
                                lpIgnore={'true'}
                            />
                        </div>
                        <div style={{ position: 'relative', top: '-4px' }} className='col-span-6 sm:col-span-6 lg:col-span-1'>
                            <label style={{ position: 'relative', top: '4px' }} className='primary'>
                                Date Of Birth
                            </label>
                            <DatePicker
                                className='block mt-1 primary'
                                selected={dobDate}
                                onChange={(date: Date) => setDobDate(date!)}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FetchInput
                                value={districtName}
                                rounded='sm'
                                placeholder=''
                                label={`District`}
                                onClear={() => setDistrictName('')}
                                onChange={(value: string) => setDistrictName(value)}
                                icon={'search'}
                            />
                        </div>

                    </div>
                    {/* Button Row */}
                    <div className="mt-6" style={{ display: 'flex' }}>
                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <SubmitButton
                                text='Search'
                                className='mr-5 primary button'
                                onClick={() => { getDuplicatesAsync.run(); }}
                            />
                        </div>

                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <button
                                className='mr-5 secondary'
                                onClick={() => { resetFields(); }}
                            >
                                Reset filter
                            </button>
                        </div>

                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <button
                                className='mr-5 primary button'
                                onClick={() => { setOpenMerge(true); }}
                                disabled={studentTo === undefined || studentFrom === undefined}
                            >
                                Open Options
                            </button>
                        </div>
                    </div>
                </div>

                {/* Results Table */}
                {studentsData !== null && studentsData !== undefined && (
                    <div className='results-table'>
                        {studentsData.length > 0 && (
                            <div className='overflow-hidden border-b border-gray-200 shadow'>
                                <table className='min-w-full divide-y divide-gray-200'>
                                    <thead className='text-left text-black bg-ptstableheader'>
                                        <tr>
                                            <th>Student Id</th>
                                            <th>Name</th>
                                            <th>District</th>
                                            <th>Date of Birth</th>
                                            <th>Added</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    {studentsData.map((row, i) => {
                                        return (
                                            <tbody className='bg-white divide-y divide-gray-200'>
                                                <tr>
                                                    <td className="py-4 pl-2">{row.studentId}</td>
                                                    <td>{row.lastName}, {row.firstName}</td>
                                                    <td>{row.DistrictName}</td>
                                                    <td>{moment(row.dob.toString()).format('MM/DD/YYYY')}</td>
                                                    <td>{row.createdOn ? moment(row.createdOn.toString()).format('MM/DD/YYYY') : "-"} by {row.createdByUserFirstName} {row.createdByUserLastName}</td>
                                                    <td rowSpan={2}>
                                                        {checkState(row.studentId) ? (
                                                            <div className="my-1" style={{ display: 'flex' }}>

                                                                {
                                                                    (studentTo === undefined) ?
                                                                        (
                                                                            <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                                                                                <button
                                                                                    className='px-2 py-1 font-bold text-white bg-green-500 hover:bg-green-700 mr-2'
                                                                                    onClick={() => { setStudentTo(row); }}
                                                                                >
                                                                                    Keep
                                                                                </button>
                                                                            </div>
                                                                        ) : ""
                                                                }
                                                                {
                                                                    (studentFrom === undefined) ?
                                                                        (
                                                                            <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                                                                                <button
                                                                                    className='px-2 py-1 font-bold text-white bg-red-500 hover:bg-red-700'
                                                                                    onClick={() => { setStudentFrom(row); }}
                                                                                >
                                                                                    Discard
                                                                                </button>
                                                                            </div>
                                                                        ) : ""
                                                                }
                                                            </div>
                                                        ) : ""}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        )
                                    })}
                                </table>
                            </div>
                        )}
                    </div>
                )}


                {/* No therapists found message */}
                {studentsData.length === 0 && (
                    <div>
                        <label>No potential duplicates found.</label>
                        <div className='px-4 py-3 bg-gray-100 sm:px-6 sm:flex sm:flex-row-reverse'>

                        </div>
                    </div>
                )}
            </Fragment>
        </Layout>

    );
};

export default ManualMerge;